import { getRequiredMessage } from 'utils/getRequiredMessage'
import {
  DIGITS_RULE,
  DIGITS_WITH_DOT_RULE,
  LATIN_LETTER_RULE,
  LATIN_LETTER_WITHOUT_SEMCOLON_RULE,
} from './uiTexts'

export const LOCATION_DETAILS = {
  FORM: {
    CONTACT_FORM_TITLE: 'Contact information',
    WORKING_HOURS_TITLE: 'Opening hours',
    RESERVATION_TITLE: 'Reservation',
    WAITING_HOURS_TITLE: 'Waiting hours',
  },
  BUTTONS: {
    CANCEL_BUTTON_LABEL: 'Cancel',
    SAVE_BUTTON_LABEL: 'Save',
    ADD_LOCATION_LABEL: 'Add location',
  },
}

export const WAITING_HOURS_PERIOD = 'Waiting hours period'
export const BUTTON_MAP_TITLE = 'Get coordinates'

export const LOCATION_ADDING_FORM_VALUES = {
  LOCATION: {
    ID: 'locationName',
    PLACEHOLDER: 'Select a location',
    LABEL: 'Location',
    REQUIRED_MESSAGE: 'Please select a location',
    SEARCH_PLACEHOLDER: 'Search for a location',
    TOOLTIP:
      'Please add the full and correct name of the location (use City name + Location type (or City area) format), e.g. Paris Charles de Gaulle Airport, Lisbon Downtown, Seville Railway Station, Barcelona Sant Boi de Liobregat, Milan Sesto San Giovanni.',
    VALIDATION_MESSAGE:
      'Location name may contain only numbers, spaces, brackets, and Latin letters.',
    HYPHEN_VALIDATION_MESSAGE:
      'Location name cannot contain hyphens when entered manually',
    IATA_TOOLTIP:
      'A location with the selected IATA code already exists. It will be used.',
  },
  LOCATION_NAME: {
    ID: 'locationName',
    LABEL: 'Add a location',
    PLACEHOLDER: 'Enter a location',
    VALIDATION_MESSAGE: LATIN_LETTER_RULE,
    MAX_VALIDATION_MESSAGE: "Can't contain more than 100 characters.",
  },
  IATA: {
    ID: 'shortCode',
    PLACEHOLDER: 'Enter IATA code',
    LABEL: 'Airport IATA (3 letters) code',
    REQUIRED_MESSAGE: getRequiredMessage('IATA code'),
    VALIDATION_MESSAGE: 'IATA code may contain only 3 capital latin letters.',
    TOOLTIP: 'Three-letter code assigned by IATA to identify the airport',
  },
  WORKING_HOURS: {
    ID: 'workingHours',
    PLACEHOLDER: 'Enter working hours',
    LABEL: 'Working hours',
    REQUIRED_MESSAGE:
      'Please enter opening hours, at least one pair of opening and closing times is required.',
    VALIDATION_MESSAGE: 'The closing time must be later than the opening time.',
    OVERLAP_MESSAGE: 'Working hours cannot overlap. Please edit your schedule.',
    PAIR_OVRELAP_MESSAGE:
      'The first set of opening hours must be earlier than the second one.',
    INCOMPLETE_INFORMATION: 'Both opening and closing times must be entered.',
    INFO: 'Please enter at least one set of times for each of your working days, or two sets if there’s a break.',
    TITLE: "Let your customers know when you're open.<mark>*</mark>",
  },
  DROPOFF_HOURS: {
    ID: 'dropoffHours',
    PLACEHOLDER: 'Enter dropoff hours',
    LABEL: 'Dropoff hours',
    REQUIRED_MESSAGE: getRequiredMessage('dropoff hours'),
  },
  LATITUDE: {
    ID: 'latitude',
    PLACEHOLDER: 'Enter latitude (e.g. 56.92628)',
    LABEL: 'Latitude',
    REQUIRED_MESSAGE: getRequiredMessage('latitude'),
    VALIDATION_MESSAGE: 'Please enter the correct format (e.g. 56.92628).',
    MIN_VALUE_MESSAGE: 'Latitude cannot be 0.',
  },
  LONGITUDE: {
    ID: 'longitude',
    PLACEHOLDER: 'Enter longitude (e.g. 23.99475)',
    LABEL: 'Longitude',
    REQUIRED_MESSAGE: getRequiredMessage('longitude'),
    VALIDATION_MESSAGE: 'Please enter the correct format (e.g. 56.92628).',
    MIN_VALUE_MESSAGE: 'Longitude cannot be 0.',
  },
  NEW_LOCATION_TYPE: {
    ID: 'newLocationType',
    PLACEHOLDER: 'Select a location type',
    LABEL: 'Type of location',
    REQUIRED_MESSAGE: 'Please select a location type',
  },
  CURRENCY: {
    ID: 'currencyName',
    PLACEHOLDER: 'Select a currency',
    LABEL: 'Currency',
    REQUIRED_MESSAGE: 'Please select a currency',
    SEARCH_PLACEHOLDER: 'Search for a currency',
  },
  PHONE_NUMBER: {
    ID: 'phone',
    PLACEHOLDER: 'Enter a phone number',
    LABEL: 'Phone number',
    REQUIRED_MESSAGE: getRequiredMessage('phone number'),
    MAX_VALIDATION_MESSAGE: "Can't contain more than 16 characters.",
  },
  PHONE_COR: {
    ID: 'phoneCor',
  },
  COUNTRY: {
    ID: 'countryId',
    PLACEHOLDER: 'Select a country',
    LABEL: 'Country',
    REQUIRED_MESSAGE: 'Please select a country',
    SEARCH_PLACEHOLDER: 'Search for a country',
    INCORRECT_COUNTRY_MESSAGE:
      'Please select the country corresponds to your location.',
  },
  CITY: {
    ID: 'cityName',
    PLACEHOLDER: 'Select a city',
    LABEL: 'City',
    REQUIRED_MESSAGE: 'Please select a city',
    SEARCH_PLACEHOLDER: 'Search for a city',
    VALIDATION_MESSAGE: LATIN_LETTER_RULE,
    HYPHEN_VALIDATION_MESSAGE:
      'City name cannot contain hyphens when entered manually',
    ADDITIONAL_VALIDATION_MESSAGE: LATIN_LETTER_WITHOUT_SEMCOLON_RULE,
  },
  CITY_NAME: {
    ID: 'cityName',
    LABEL: 'Add a city',
    PLACEHOLDER: 'Enter a city',
    VALIDATION_MESSAGE: LATIN_LETTER_RULE,
    MAX_VALIDATION_MESSAGE: "Can't contain more than 100 characters.",
    ADDITIONAL_VALIDATION_MESSAGE: LATIN_LETTER_WITHOUT_SEMCOLON_RULE,
  },
  APARTMENT: {
    ID: 'apartment',
    PLACEHOLDER: 'Enter apartment',
    LABEL: 'Apartment',
  },
  STREET: {
    ID: 'street',
    PLACEHOLDER: 'Enter a street (e.g. Main Street)',
    LABEL: 'Street',
    REQUIRED_MESSAGE: getRequiredMessage('street'),
    VALIDATION_MESSAGE: LATIN_LETTER_RULE,
    MAX_VALIDATION_MESSAGE: "Can't contain more than 100 characters.",
    ADDITIONAL_VALIDATION_MESSAGE: LATIN_LETTER_WITHOUT_SEMCOLON_RULE,
  },
  STATE: {
    ID: 'stateId',
    PLACEHOLDER: 'Enter a state / region',
    SELECT_PLACEHOLDER: 'Select a state',
    LABEL: 'State / Region',
    SELECT_LABEL: 'State',
    REQUIRED_MESSAGE: getRequiredMessage('state / region'),
    SELECT_REQUIRED_MESSAGE: 'Please select a state.',
    VALIDATION_MESSAGE: LATIN_LETTER_RULE,
    SEARCH_PLACEHOLDER: 'Search for a state',
    ADDITIONAL_VALIDATION_MESSAGE: LATIN_LETTER_WITHOUT_SEMCOLON_RULE,
  },
  BUILDING: {
    ID: 'building',
    PLACEHOLDER: 'Enter a building (e.g. 11)',
    LABEL: 'Building',
    REQUIRED_MESSAGE: getRequiredMessage('building'),
    VALIDATION_MESSAGE: LATIN_LETTER_RULE,
    MAX_VALIDATION_MESSAGE: "Can't contain more than 30 characters.",
    ADDITIONAL_VALIDATION_MESSAGE: LATIN_LETTER_WITHOUT_SEMCOLON_RULE,
  },
  WAITING_HOURS: {
    ID: 'waitingHours',
    PLACEHOLDER: '0',
    LABEL: 'Waiting hours',
    REQUIRED_MESSAGE: getRequiredMessage('waiting hours'),
    VALIDATION_MESSAGE: DIGITS_RULE,
    TOOLTIP:
      'How long are you willing to wait for the customer to pick up/drop off the car outside of your opening hours.',
  },
  WAITING_HOURS_FEE_TYPE: {
    ID: 'waitingHourFeeType',
    LABEL: 'Fee types',
  },
  WAITING_HOURS_RANGE: {
    ID: 'waitingHourRange',
    LABEL: 'Waiting hours period',
  },
  POSTAL_CODE: {
    ID: 'postalCode',
    PLACEHOLDER: 'Enter a postal code',
    LABEL: 'Postal code/Postcode/ ZIP code',
    REQUIRED_MESSAGE: getRequiredMessage('Postal code / Postcode / ZIP code'),
    VALIDATION_MESSAGE: LATIN_LETTER_RULE,
    MAX_VALIDATION_MESSAGE: "Can't contain more than 16 characters.",
    ADDITIONAL_VALIDATION_MESSAGE: LATIN_LETTER_WITHOUT_SEMCOLON_RULE,
  },
  PRICE: {
    ID: 'waitingHourPrice',
    PLACEHOLDER: 'e.g. 25; 15.99',
    LABEL: 'Price',
    VALIDATION_MESSAGE: DIGITS_WITH_DOT_RULE,
  },
  EMAIL: {
    ID: 'email',
    PLACEHOLDER: 'Enter your email',
    LABEL: 'Email',
    REQUIRED_MESSAGE: 'Please enter your email.',
    VALIDATION_MESSAGE: 'Please enter a valid email.',
    TOOLTIP:
      'Your email address is required so that we can send you booking requests.',
    MAX_VALIDATION_MESSAGE: "Can't contain more than 254 characters.",
  },
  MINIMUM_PERIOD_DAY: {
    ID: 'minimumPeriodDay',
    PLACEHOLDER: '0',
    LABEL: 'Minimum number of rental days',
    VALIDATION_MESSAGE:
      'Minimum number of rental days must be greater or equal to 1 day.',
  },
  MAXIMUM_PERIOD_DAY: {
    ID: 'maximumPeriodDay',
    PLACEHOLDER: '0',
    LABEL: 'Maximum number of rental days',
  },
  PICKUP_OPTION: {
    ID: 'pickupOption',
    LABEL: 'Pick-up procedure',
    PLACEHOLDER: 'Select pick-up procedure',
  },
  PICKUP_INSTRUCTIONS: {
    ID: 'pickUpInstruction',
    LABEL: 'Where to pick up',
    PLACEHOLDER:
      'For example: the rental desk is located on the first floor in Terminal A. Upon arrival, proceed to the car rental area near exit 4 and look for the yellow signboard “John Doe Car Rentals”.',
    VALIDATION_MESSAGE: LATIN_LETTER_RULE,
    MAX_VALIDATION_MESSAGE: "Can't contain more than 700 characters.",
    TOOLTIP:
      'Provide clear guidance on where the renter can pick up the rental car. Specify terminal, exit, floor, signboard name & color and other relevant information to make the pick-up process easy and hassle-free.',
  },
  DROPOFF_INSTRUCTIONS: {
    ID: 'dropOffInstruction',
    LABEL: 'Where to drop off',
    PLACEHOLDER:
      'For example: the rental desk is located on the first floor in Terminal A. Upon arrival at the airport, proceed to the car rental area near entrance 4 and look for the yellow signboard “John Doe Car Rentals”.',
    VALIDATION_MESSAGE: LATIN_LETTER_RULE,
    MAX_VALIDATION_MESSAGE: "Can't contain more than 700 characters.",
    TOOLTIP:
      'Provide clear guidance on where the renter can drop off the rental car. Specify terminal, entrance, floor, signboard name & color and other relevant information to make the drop-off process easy and hassle-free.',
  },
  START_RESERVATION_HOUR: {
    ID: 'startReservationHour',
    LABEL: 'Max. advance reservation hours for last minute bookings',
    PLACEHOLDER: 'Please enter the number of hours (e.g. 12, 24, 48 or 72)',
    REQUIRED_MESSAGE: 'Please enter start reservation hour.',
    TOOLTIP:
      'Specify the number of hours prior to last minute pickup when the car will no longer be available for booking and should not be displayed on the website.',
  },
  DRIVER_AGE_RANGE: {
    ID: 'driverAgeRange',
    REQUIRED_MESSAGE: 'Please complete all the fields.',
  },
  MIN_MAX_RENTAL_DAYS: {
    ID: 'minMaxPeriodDay',
    VALIDATION_MESSAGE:
      'The maximum number must be equal or greater than the minimum.',
  },
}

export const LIMIT_INFO =
  'The maximum number of locations that can be added for initial approval is 3.'

export const NEW_LOCATION_INFO =
  'Please proceed with filling other information, location will get approved.'

export const LOCATIONS_TABLE_ITEMS = [
  LOCATION_ADDING_FORM_VALUES.COUNTRY.LABEL,
  LOCATION_ADDING_FORM_VALUES.CITY.LABEL,
  LOCATION_ADDING_FORM_VALUES.LOCATION.LABEL,
  LOCATION_ADDING_FORM_VALUES.STREET.LABEL,
  LOCATION_ADDING_FORM_VALUES.BUILDING.LABEL,
  LOCATION_ADDING_FORM_VALUES.PICKUP_OPTION.LABEL,
]

export const NEXT_BUTTON_TITLE = 'Next'

export const TABLE_INFO = {
  TITLE: 'Your locations',
  SUBTITLE: 'Manage the locations you have added.',
  BUTTON_LABEL: 'Complete',
}

export const LOCATION_DELETE_CONFIRMATION = {
  CONFIRMATION_MODAL: {
    QUESTION: 'Are you sure that you want to delete',
    BUTTON_LABEL: 'Confirm',
    TITLE: 'Delete',
  },
  SUCCESS: 'The location has been successfully deleted',
}

export const SUCCESS_LOCATION_UPDATE =
  'Your locations have been successfully saved '

export const LOCATION_WEEK_DAY = {
  START_WORK: 'Opening time',
  START_LUNCH: 'Start lunch',
  FINISH_LUNCH: 'Finish lunch',
  FINISH_WORK: 'Closing time',
}

export const LOCATION_TABS = {
  INFORMATION: 'Details',
  WORKING_HOURS: 'Opening hours',
  RESERVATION: 'Reservation',
  WAITING_HOURS: 'Outside of Working hours',
}
export const PICKUP_OPTIONS = {
  DEFAULT: {
    ID: 'Default',
    LABEL: 'No option selected',
  },
  DESK_IN_TERMINAL: {
    ID: 'DeskInTerminal',
    LABEL: 'Desk in terminal',
  },
  SHUTTLE_TO_LOCATION: {
    ID: 'ShuttleToLocation',
    LABEL: 'Shuttle to location',
  },
  MEETING_IN_AIRPORT: {
    ID: 'MeetingInAirport',
    LABEL: 'Meeting in airport',
  },
}

export const WAITING_HOURS_RANGE = {
  BOTH: {
    ID: 'Both',
    LABEL: 'Both',
  },
  PICK_UP: {
    ID: 'PickUp',
    LABEL: 'Pick-up',
  },
  DROP_OFF: {
    ID: 'DropOff',
    LABEL: 'Drop-off',
  },
}

export const NO_COORDINATES =
  'Unable to get latitude and longitude. Please enter the values manually.'

export const MAP_MODAL = {
  TITLE: 'Add another location',
  BUTTON_LABEL: 'Add your location here',
  INPUT_LABEL: 'Your location',
  INPUT_PLACEHOLDER: 'Find your location',
  BUTTON_QUESTION: 'Haven’t found location?',
  CONFIRM_BUTTON_LABEL: 'Confirm your location',
}
